import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  computed,
  inject,
  input,
  model,
  output,
  signal,
} from '@angular/core';
import { A11yModule } from '@angular/cdk/a11y';
import { RouterLink } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { BugTargetPickerComponent } from '../../../components/bug-target-picker/bug-target-picker.component';
import {
  BugTargetChoice,
  NO_BUG_TARGET_CHOICE,
  resolveBugTargets,
} from '../../../components/bug-target-picker/bug-target-choice';
import { SectionTitleComponent } from '../../../components/section-title/section-title.component';
import { KiPipelineAktionVorlage } from '../../../models/ki.model';
import { ProjectIntegration } from '../../../models/project-integration.model';
import { CreateBugFromStepResultRequest, PipelineFehlerKategorie } from '../../../models/test-run.model';
import type { BugPreviewState } from '../../../services/case-result-bug-flow';
import { FIELD_LENGTHS } from '../../../shared/field-lengths';
import { LiveBugError } from '../live-bug-error';

export const LIVE_BUG_DIALOG_KEYS = {
  title: 'testRunner.live.bugDialog.title',
  close: 'testRunner.live.bugDialog.close',
  subjectLabel: 'testRunner.live.bugDialog.subjectLabel',
  descriptionLabel: 'testRunner.live.bugDialog.descriptionLabel',
  previewLabel: 'testRunner.live.bugDialog.previewLabel',
  previewLoading: 'testRunner.live.bugDialog.previewLoading',
  previewFailed: 'testRunner.live.bugDialog.previewFailed',
  targetLabel: 'testRunner.live.bugDialog.targetLabel',
  existing: 'testRunner.live.bugDialog.existing',
  existingAria: 'testRunner.live.bugDialog.existingAria',
  toAccount: 'testRunner.live.bugDialog.toAccount',
  cancel: 'testRunner.live.bugDialog.cancel',
  submit: 'testRunner.live.bugDialog.submit',
  draftSubject: 'testRunner.live.bugDialog.draft.subject',
  draftAutoId: 'testRunner.live.bugDialog.draft.autoId',
  draftTest: 'testRunner.live.bugDialog.draft.test',
  draftRun: 'testRunner.live.bugDialog.draft.run',
  draftRunNoKey: 'testRunner.live.bugDialog.draft.runNoKey',
  draftPipeline: 'testRunner.live.bugDialog.draft.pipeline',
  draftKiAnalyse: 'testRunner.live.bugDialog.draft.kiAnalyse',
  draftKiUrsache: 'testRunner.live.bugDialog.draft.kiUrsache',
  draftSubjectAusfuehrung: 'testRunner.live.bugDialog.draft.subjectAusfuehrung',
  anhangHinweis: 'testRunner.live.bugDialog.anhangHinweis',
  anhangHinweisOhneVorschau: 'testRunner.live.bugDialog.anhangHinweisOhneVorschau',
  existingAusfuehrung: 'testRunner.live.bugDialog.existingAusfuehrung',
  alreadyLinkedAusfuehrung: 'testRunner.live.bugDialog.error.alreadyLinkedAusfuehrung',
} as const;

/** Wozu der Vorgang entsteht — festgehalten beim Oeffnen, nicht nachgefuehrt. */
export interface LiveBugContext {
  autoId: string;
  testName: string;
  runKey: string | null;
  executionNr: number;
  /** Name samt Build-Nummer, wie ihn die Pipeline-Karte zeigt; {@code null} ohne CI-Lauf. */
  pipeline: string | null;
  /** Nur aus der KI-Ursachenanalyse des Laufs geoeffnet (NEX-1548). */
  kiAnalyse?: LiveBugKiAnalyse;
  /** Nur fuer einen Bug am Durchlauf statt am Fall. */
  ausfuehrung?: LiveBugAusfuehrung;
}

/** Der Durchlauf, an dem der Vorgang entsteht, wenn es keinen Fall dafuer gibt. */
export interface LiveBugAusfuehrung {
  kategorie: PipelineFehlerKategorie;
  /** Was der Server beim Anlegen anhaengt (`bug-preview`); {@code null}, wo die Vorschau fehlt. */
  anhang: string | null;
}

/** Der Durchlauf, wie ihn Titel und Untertitel nennen: Schluessel des Laufs und Nummer. */
function runLabel({ runKey, executionNr }: LiveBugContext): string {
  return runKey ? `${runKey} · #${executionNr}` : `#${executionNr}`;
}

export type LiveBugKiAnalyse = Pick<KiPipelineAktionVorlage, 'kategorie' | 'zusammenfassung' | 'ursache'>;

/**
 * Titel, Text und Ziel eines Bug-Vorgangs aus einer Fehlerzeile der Live-Ansicht
 * oder, ohne fehlgeschlagenen Fall, fuer den Durchlauf selbst.
 *
 * <p>Im Feature-Ordner und nicht unter {@code components/}: der Dialog kennt nur die
 * Fehlerzeile und den Durchlauf der Live-Ansicht, und die Tracker-Wahl, die auch andere brauchen,
 * liegt schon im gemeinsamen {@link BugTargetPickerComponent}.
 *
 * <p>Die Fehlermeldung schreibt der Dialog nicht in den Text: sie verlaesst den Server
 * nicht, er haengt sie beim Anlegen selbst an (NEX-1356).
 *
 * <p>Gebaut nach dem Muster von {@code test-run-handover-modal}; warum nicht
 * {@code lib-modal}, steht dort. Der Aufrufer haelt den Aufruf offen und meldet ihn ueber
 * {@link submitting} zurueck, der Dialog schliesst sich nicht selbst.
 */
@Component({
  selector: 'app-live-bug-dialog',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    A11yModule,
    RouterLink,
    MatIconModule,
    MatProgressSpinnerModule,
    TranslatePipe,
    BugTargetPickerComponent,
    SectionTitleComponent,
  ],
  templateUrl: './live-bug-dialog.component.html',
  styleUrl: './live-bug-dialog.component.scss',
})
export class LiveBugDialogComponent implements OnInit {
  private readonly translate = inject(TranslateService);

  readonly context = input.required<LiveBugContext>();
  readonly targets = input.required<readonly ProjectIntegration[]>();
  readonly submitting = input(false);
  readonly error = input<LiveBugError | null>(null);
  /** Der Vorgang, der schon am Fall haengt — etwa von jemand anderem angelegt. */
  readonly existingBugKey = input<string | null>(null);
  readonly existingBugUrl = input<string | null>(null);
  /** Was der Server beim Anlegen an den Vorgang haengt; {@code null}, wo der Aufrufer keine Vorschau fuehrt. */
  readonly preview = input<BugPreviewState | null>(null);
  readonly choice = model<BugTargetChoice>(NO_BUG_TARGET_CHOICE);

  readonly confirmed = output<CreateBugFromStepResultRequest>();
  readonly cancelled = output<void>();

  readonly keys = LIVE_BUG_DIALOG_KEYS;
  readonly fieldLengths = FIELD_LENGTHS;

  readonly subject = signal('');
  readonly description = signal('');

  private readonly prefill = signal({ subject: '', description: '' });

  /** Gegenueber der Vorbelegung geaendert — ein zurueckgenommener Eingriff zaehlt nicht. */
  private readonly changed = computed(
    () => this.subject() !== this.prefill().subject || this.description() !== this.prefill().description,
  );

  readonly subtitle = computed(() => {
    const context = this.context();
    if (context.ausfuehrung) {
      return `${this.kategorieLabel(context.ausfuehrung)} · ${runLabel(context)}`;
    }
    return context.testName ? `${context.autoId} · ${context.testName}` : context.autoId;
  });

  /** Am Durchlauf gibt es keine Fehlermeldung eines Tests; dort zeigt der Dialog, was der Server anhaengt. */
  readonly ausfuehrung = computed(() => this.context().ausfuehrung ?? null);

  /** Am Fall zeigt der Dialog die Fehlermeldung, die der Server anhaengt; ohne Meldung fehlt der Bereich ganz. */
  readonly previewArea = computed(() => {
    const preview = this.preview();
    if (!preview || this.ausfuehrung()) return null;
    return preview.status === 'ready' && !preview.text ? null : preview;
  });

  readonly existingKey = computed(() => (this.ausfuehrung() ? this.keys.existingAusfuehrung : this.keys.existing));

  /** Ein 409 am Durchlauf betrifft keinen Test und keine Zeile; die uebrigen Absagen gelten fuer beide Wege. */
  readonly errorKey = computed(() => {
    const error = this.error();
    return error?.alreadyLinked && this.ausfuehrung() ? this.keys.alreadyLinkedAusfuehrung : (error?.key ?? null);
  });

  private readonly targetOptions = computed(() => resolveBugTargets(this.targets(), this.choice()));
  private readonly target = computed(() => this.targetOptions().target);
  readonly offersTargetChoice = computed(() => this.targetOptions().offersChoice);

  /** Ein zweiter Versuch an einer schon verknuepften Zeile scheiterte nur erneut am Server. */
  private readonly linked = computed(() => this.error()?.alreadyLinked === true || this.existingBugKey() !== null);

  readonly canSubmit = computed(
    () => this.subject().trim().length > 0 && !this.submitting() && !this.linked() && this.target() !== null,
  );

  /**
   * Die Vorbelegung entsteht einmal, beim Oeffnen: laeuft der Feed weiter, darf ein
   * neuer Stand nicht ueberschreiben, was schon getippt ist.
   */
  ngOnInit(): void {
    const context = this.context();
    const prefill = { subject: this.draftSubject(context), description: this.draftDescription(context) };
    this.prefill.set(prefill);
    this.subject.set(prefill.subject);
    this.description.set(prefill.description);
  }

  submit(): void {
    if (!this.canSubmit()) return;
    const description = this.description().trim();
    this.confirmed.emit({
      subject: this.subject().trim(),
      ...(description ? { description } : {}),
      integrationId: this.target()?.id,
    });
  }

  cancel(): void {
    if (this.submitting()) return;
    this.cancelled.emit();
  }

  /** Ein Fehlklick neben den Dialog verwirft keine Eingaben; Escape und Abbrechen schon. */
  dismissFromBackdrop(): void {
    if (this.changed()) return;
    this.cancel();
  }

  /** Wie der Server-Vorschlag {@code buildDefaultSubject}, gekuerzt auf dieselbe Laenge. */
  private draftSubject(context: LiveBugContext): string {
    const subject: string = context.ausfuehrung
      ? this.translate.instant(LIVE_BUG_DIALOG_KEYS.draftSubjectAusfuehrung, {
          kategorie: this.kategorieLabel(context.ausfuehrung),
          run: runLabel(context),
        })
      : this.translate.instant(LIVE_BUG_DIALOG_KEYS.draftSubject, { test: context.testName || context.autoId });
    return subject.slice(0, FIELD_LENGTHS.NAME_MAX);
  }

  private kategorieLabel({ kategorie }: LiveBugAusfuehrung): string {
    return this.translate.instant(`pipeKategorie.${kategorie}.label`);
  }

  private draftDescription(context: LiveBugContext): string {
    const line = (key: string, params: Record<string, unknown>): string => this.translate.instant(key, params);
    const lines: string[] = [];
    if (!context.ausfuehrung) {
      lines.push(line(LIVE_BUG_DIALOG_KEYS.draftAutoId, { value: context.autoId }));
    }
    if (context.testName) {
      lines.push(line(LIVE_BUG_DIALOG_KEYS.draftTest, { value: context.testName }));
    }
    lines.push(
      context.runKey
        ? line(LIVE_BUG_DIALOG_KEYS.draftRun, { run: context.runKey, nr: context.executionNr })
        : line(LIVE_BUG_DIALOG_KEYS.draftRunNoKey, { nr: context.executionNr }),
    );
    if (context.pipeline) {
      lines.push(line(LIVE_BUG_DIALOG_KEYS.draftPipeline, { value: context.pipeline }));
    }
    const ki = context.kiAnalyse;
    if (ki) {
      const kategorie = this.translate.instant(`pipeKategorie.${ki.kategorie}.label`);
      lines.push(
        '',
        line(LIVE_BUG_DIALOG_KEYS.draftKiAnalyse, { kategorie, text: ki.zusammenfassung }),
        line(LIVE_BUG_DIALOG_KEYS.draftKiUrsache, { text: ki.ursache }),
      );
    }
    return lines.join('\n').slice(0, FIELD_LENGTHS.FREETEXT_MAX);
  }
}
